import { useState, type FormEvent } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { ShieldCheck } from "lucide-react";
import { ApiError } from "@/lib/api";
import { useAuthStore } from "@/store/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

export function ChangePasswordPage() {
  const navigate = useNavigate();
  const token = useAuthStore((state) => state.passwordChangeToken);
  const status = useAuthStore((state) => state.status);
  const complete = useAuthStore((state) => state.completePasswordChange);
  const [password, setPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (status === "authenticated") return <Navigate to="/" replace />;
  if (!token) return <Navigate to="/login" replace />;

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (password !== confirmation) return setError("Passwords do not match.");
    setLoading(true);
    setError(null);
    try {
      await complete(password);
      navigate("/", { replace: true });
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : cause instanceof Error ? cause.message : "Password could not be changed.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="mobile-safe-top mobile-safe-bottom flex min-h-dvh items-center justify-center px-4 py-6">
      <form onSubmit={submit} className="w-full max-w-sm space-y-4 rounded-2xl border border-studio-border bg-studio-panel p-5 shadow-panel sm:p-6">
        <div className="flex items-start gap-3"><span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-studio-accent-soft text-studio-accent"><ShieldCheck className="h-5 w-5" /></span><div><h1 className="text-wrap-balance text-lg font-semibold text-studio-ink">Replace your temporary password</h1><p className="mt-1 text-xs leading-5 text-studio-muted">You must set a private password before opening team data.</p></div></div>
        <label className="block text-xs font-medium text-studio-muted">New password<Input className="mt-1.5" type="password" autoComplete="new-password" minLength={8} required value={password} onChange={(event) => setPassword(event.target.value)} /></label>
        <label className="block text-xs font-medium text-studio-muted">Confirm password<Input className="mt-1.5" type="password" autoComplete="new-password" minLength={8} required value={confirmation} onChange={(event) => setConfirmation(event.target.value)} /></label>
        {error && <p role="alert" className="rounded-lg bg-studio-danger/10 px-3 py-2 text-xs text-studio-danger">{error}</p>}
        <Button className="w-full active:scale-[0.96] transition-transform" size="lg" type="submit" loading={loading}>Save password</Button>
      </form>
    </div>
  );
}
